import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { UserPlusIcon, ArrowDownIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import BugReportIcon from "@mui/icons-material/BugReport"; //bug
import RuleIcon from "@mui/icons-material/Rule";
import DescriptionIcon from "@mui/icons-material/Description"; //audit
import BlurOnIcon from "@mui/icons-material/BlurOn"; //other
import AutoAwesomeTwoToneIcon from "@mui/icons-material/AutoAwesomeTwoTone"; //feature

import {
  Dialog,
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
  Select,
  Option,
  Chip,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import {
  getServiceRequestData,
  createServiceRequestData,
} from "../../helperFunctions/apiFunction";
import LoadingButton from "../../components/Loaders/LoadingButton";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import ExcelJS from "exceljs";
import moment from "moment";

const TABLE_HEAD = [
  "Id",
  "Type",
  "Details",
  "Status",
  "Expected Start Date",
  "Expected End Date",
];

export default function ServiceRequests() {
  const { refreshToken, rbac } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [errors, setErrors] = useState({ name: "" });
  const [requestType, setRequestType] = useState("");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog((cur) => !cur);
  };
  useEffect(() => {
    getServiceRequestData(
      refreshToken,
      searchBy,
      sortBy,
      limit,
      page,
      status,
      requestType
    ).then((data) => {
      if (data?.code === 440)
        toast.error(data?.message, {
          className: "text-[12px]",
        });
      setTotalPage(data?.data?.totalPages);
      setTemplateData(data?.data?.results);
      setIsLoading(false);
    });
  }, [page, limit, sortBy, searchBy, status, reloadData, requestType]);

  // Create Form Functionality
  const [formData, setFormData] = useState({
    serviceType: "",
    serviceDescription: "",
    serviceStartDate: "",
    serviceEndDate: "",
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.serviceType.trim())
      newErrors.serviceType = "Service Type is required";
    if (!formData.serviceDescription.trim())
      newErrors.serviceDescription = "Service Description is required";
    if (!formData.serviceStartDate.trim())
      newErrors.serviceStartDate = "Service Start Date is required";
    if (!formData.serviceEndDate.trim())
      newErrors.serviceEndDate = "Service End Date is required";
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeSelect = (e, fieldName) => {
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: e,
    }));
  };

  const handleBlur = () => {
    validateForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    validateForm();
    if (Object.keys(errors).length === 0) {
      setIsLoadingButton(true);
      createServiceRequestData(refreshToken, formData)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            toast.success("Service Request created successfully", {
              className: "text-[12px]",
            });
          } else if (response.code === 400) {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          } else {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error("Something went wrong!", {
            className: "text-[12px]",
          });
          console.error("Error during login:", error);
        })
        .finally(() => {
          setIsLoadingButton(false);
          setOpenCreateDialog((cur) => !cur);
          setReloadData((cur) => !cur);
        });
    }
  };
  // End Create Form Functionality

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("ServiceRequests Data");
    const headers = [
      "S.No.",
      "Service Type",
      "Service Description",
      "Service Start Date",
      "Service End Data",
    ];
    worksheet.addRow(headers);
    templateData.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.serviceType,
        row.serviceDescription,
        row.serviceStartDate,
        row.serviceEndDate,
      ]);
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "serviceRequests_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="app-main__inner !pl-20">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Service Requests
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all your service requests
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={exportToExcel}
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
              {rbac?.serviceAdd ? (
                <>
                  <Button
                    onClick={handleOpenCreateDialog}
                    className="flex items-center gap-3"
                    size="sm"
                    loading={isLoading === null ? true : false}
                  >
                    <UserPlusIcon strokeWidth={2} className="h-4 w-4" />
                    <span className="hidden md:inline-block"> Create New</span>
                  </Button>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                label="Select Limit"
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="purple"
                label="Sort By"
                className="!border-t-0 !border-[#b0bec5]"
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event);
                  setIsLoading(null);
                }}
              >
                <Option value="asc">Ascending</Option>
                <Option value="desc">Descending</Option>
              </Select>
              <Select
                color="green"
                label="Type"
                value={requestType}
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setRequestType(event);
                  setIsLoading(null);
                }}
              >
                <Option value="">All Data</Option>
                <Option value="Feature">Feature</Option>
                <Option value="Bug">Bug</Option>
                <Option value="Audit">Audit</Option>
                <Option value="Testing">Testing</Option>
                <Option value="Other">Other</Option>
              </Select>
              <Select
                color="yellow"
                label="Status"
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setStatus(event);
                  setIsLoading(null);
                }}
                value={status}
              >
                <Option value="">Select Status</Option>
                <Option value="scheduled">Schedule</Option>
                <Option value="inProgress">In Progress</Option>
                <Option value="completed">Completed</Option>
                <Option value="onHold">On Hold</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                value={searchBy}
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {TABLE_HEAD.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                    >
                      {head}{" "}
                      {index !== TABLE_HEAD.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={TABLE_HEAD.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <>
                <tbody>
                  {isLoading === false && templateData?.length > 0 ? (
                    templateData.map((item, index) => {
                      if (!item) return null;
                      const {
                        serviceType,
                        serviceDescription,
                        serviceStartDate,
                        serviceEndDate,
                        serviceStatus,
                        _id,
                      } = item;
                      const isLast = index === templateData.length - 1;
                      const classes = isLast
                        ? "p-4"
                        : "p-4 border-b border-blue-gray-50";

                      return (
                        <tr key={`${_id}-${index}`}>
                          <td className={classes}>
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-normal"
                            >
                              {index + 1}
                            </Typography>
                          </td>
                          <td className={classes}>
                            <div className="w-max">
                              <Chip
                                variant="outlined"
                                className={`py-1.5 ${serviceType === "Audit"
                                  ? "!border-blue-500 !text-blue-600"
                                  : serviceType === "Feature"
                                    ? "!border-green-500 !text-green-600"
                                    : serviceType === "Bug"
                                      ? "!text-red-600 !border-red-600"
                                      : serviceType === "Testing"
                                        ? "!border-purple-500 !text-purple-600"
                                        : serviceType === "Other"
                                          ? "!border-amber-600 !text-amber-600"
                                          : "!border-blue-gray-500 !text-blue-gray-600"
                                  }`}
                                icon={
                                  serviceType === "Bug" ? (
                                    <BugReportIcon className="p-[2px]" />
                                  ) : serviceType === "Feature" ? (
                                    <AutoAwesomeTwoToneIcon className="p-[2px]" />
                                  ) : serviceType === "Testing" ? (
                                    <RuleIcon className="p-[2px]" />
                                  ) : serviceType === "Audit" ? (
                                    <DescriptionIcon className="p-[2px]" />
                                  ) : serviceType === "Other" ? (
                                    <BlurOnIcon className="p-[2px]" />
                                  ) : null
                                }
                                value={
                                  serviceType === "Bug"
                                    ? "Bug"
                                    : serviceType === "Feature"
                                      ? "Feature"
                                      : serviceType === "Testing"
                                        ? "Testing"
                                        : serviceType === "Audit"
                                          ? "Audit"
                                          : serviceType === "Other"
                                            ? "Other"
                                            : null
                                }
                              />
                            </div>
                          </td>
                          <td className={classes}>
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-normal"
                            >
                              {serviceDescription}
                            </Typography>
                          </td>
                          <td className={classes}>
                            <div className="w-max">
                              <Chip
                                variant="ghost"
                                size="sm"
                                value={
                                  serviceStatus === "scheduled"
                                    ? "Schedule"
                                    : serviceStatus === "inProgress"
                                      ? "In Progress"
                                      : serviceStatus === "completed"
                                        ? "Completed"
                                        : serviceStatus === "onHold"
                                          ? "On Hold"
                                          : serviceStatus === "cancelled"
                                            ? "Cancelled"
                                            : "None"
                                }
                                color={
                                  serviceStatus === "scheduled"
                                    ? "blue"
                                    : serviceStatus === "inProgress"
                                      ? "yellow"
                                      : serviceStatus === "completed"
                                        ? "green"
                                        : serviceStatus === "onHold"
                                          ? "orange"
                                          : serviceStatus === "cancelled"
                                            ? "red"
                                            : "blue-gray"
                                }
                              />
                            </div>
                          </td>
                          <td className={classes}>
                            <Chip
                              variant="ghost"
                              size="sm"
                              value={
                                serviceStartDate
                                  ? moment(serviceStartDate).format(
                                    "MMMM DD, YYYY"
                                  )
                                  : ""
                              }
                              className="rounded-full w-fit text-xs	normal-case font-medium	"
                            />
                          </td>
                          <td className={classes}>
                            <Chip
                              variant="ghost"
                              size="sm"
                              value={
                                serviceEndDate
                                  ? moment(serviceEndDate).format(
                                    "MMMM DD, YYYY"
                                  )
                                  : ""
                              }
                              className="rounded-full w-fit text-xs	normal-case font-medium	"
                            />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="flex flex-col items-center w-full">
                          <img
                            quality={100}
                            src="/assets/images/9276430.jpg"
                            className="w-[350px] mix-blend-darken"
                            alt="no-data-found"
                          />
                          <p className="text-[20px] font-medium">
                            No Data Found!
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page - 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setPage(index + 1);
                      setIsLoading(null);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>

      {/* create new request */}
      <Dialog
        size="sm"
        open={openCreateDialog}
        handler={() => setOpenCreateDialog(false)}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <Card className="mx-auto w-full">
          <form onSubmit={handleSubmit}>
            <CardBody className="flex flex-col gap-4">
              <Typography variant="h4" color="blue-gray">
                Add New Request
              </Typography>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Select
                    color="green"
                    label="Type"
                    name="serviceType"
                    className="!border-t-0 !border-[#b0bec5]"
                    onBlur={handleBlur}
                    value={formData?.serviceType || ""}
                    onChange={(e) => handleChangeSelect(e, "serviceType")}
                  >
                    <Option value="">All Data</Option>
                    <Option value="Feature">Feature</Option>
                    <Option value="Bug">Bug</Option>
                    <Option value="Audit">Audit</Option>
                    <Option value="Testing">Testing</Option>
                    <Option value="Other">Other</Option>
                  </Select>
                  {errors.serviceType && (
                    <p className="text-red-900 text-[12px]">
                      {errors.serviceType}
                    </p>
                  )}
                </div>
                <div>
                  <Input
                    type="text"
                    label="Detailed Description"
                    size="lg"
                    name="serviceDescription"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="focus:!border-black focus:!border-2 focus:!border-t-0"
                  />
                  {errors.serviceDescription && (
                    <p className="text-red-900 text-[12px]">
                      {errors.serviceDescription}
                    </p>
                  )}
                </div>

                <div>
                  <Input
                    type="date"
                    label="Start Date"
                    name="serviceStartDate"
                    id="serviceStartDate"
                    size="lg"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="focus:!border-black focus:!border-2 !border-t-0 focus:!border-t-0"
                  />
                </div>
                {errors.serviceStartDate && (
                  <p className="text-red-900 text-[12px]">
                    {errors.serviceStartDate}
                  </p>
                )}
                <div>
                  <Input
                    type="date"
                    label="End Date"
                    name="serviceEndDate"
                    id="serviceEndDate"
                    size="lg"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="focus:!border-black focus:!border-2 !border-t-0 focus:!border-t-0"
                  />
                </div>
                {errors.serviceEndDate && (
                  <p className="text-red-900 text-[12px]">
                    {errors.serviceEndDate}
                  </p>
                )}
              </div>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleOpenCreateDialog}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>

              {isLoadingButton ? (
                <>
                  <LoadingButton isSetText={true} isLoading={isLoading} />
                </>
              ) : (
                <Button
                  variant="gradient"
                  color="green"
                  type="submit"
                // onClick={handleOpenCreateDialog}
                >
                  <span>Add</span>
                </Button>
              )}
            </CardFooter>
          </form>
        </Card>
      </Dialog>
    </div>
  );
}
